import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/authStore';

/**
 * Centralized Axios instance for API requests
 * Configured with base URL, credentials, and interceptors
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: true,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor for additional configuration
 * Kratos uses cookies for authentication, so no token header needed
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Kratos handles authentication via cookies automatically
    // No need to add Authorization header
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

/**
 * Response interceptor for error handling.
 *
 * - 401  → no session anymore (cookie expired / revoked). Clear local
 *          state and go to login.
 * - 403 + `error.id: session_aal2_required` → the user's session is
 *          aal1 but the endpoint demands aal2. Redirect to the step-up
 *          flow with a return path so the operation can be retried
 *          after MFA. Mirrors the URL shape `useEnsureAal` uses.
 *          Any other 403 is left for the calling code to handle (it's
 *          a normal authorization failure, not an MFA shortfall).
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    if (status === 401) {
      useAuthStore.getState().clearSession();
      window.location.href = '/auth/login';
      return Promise.reject(error);
    }

    // AAL2 step-up. The BFF signals this with the
    // {error:{id:"session_aal2_required"}} body marker. It normally arrives as
    // 403, but endpoints fronted by CloudFront use 428 so the marker survives
    // (CloudFront rewrites 403/404 → 200 /index.html for SPA deep-linking).
    // Match on the marker regardless of the exact status.
    const body = error.response?.data as
      | { error?: { id?: string } }
      | undefined;
    if (body?.error?.id === 'session_aal2_required') {
      const returnTo = encodeURIComponent(
        window.location.pathname + window.location.search,
      );
      window.location.href = `/auth/login?aal=aal2&return_to=${returnTo}`;
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);
